import type { Point2D } from '@shared/models/geometry.model';
import { roundToDecimals } from '@shared/helpers/math.helpers';
import type { PointDragSnapGuides } from '../models/map-observables.models';

const MIN_BISECTOR_OPENING_DEGREES = 10;
const MIN_DIRECTION_LENGTH = 1e-8;

/** Capture infinite adjacent-edge axes at the original vertex, plus its angle bisector. */
export const getPointDragSnapGuides = (
  points: readonly Point2D[],
  index: number,
  isDraft: boolean,
  pickupOrigin?: Point2D,
): PointDragSnapGuides | undefined => {
  const origin = points[index];
  if (!origin || points.length < 2) return undefined;
  const firstNeighbor = isDraft ? undefined : points.at(-1);
  const lastNeighbor = isDraft ? undefined : points[0];
  const neighbors = [
    index > 0 ? points[index - 1] : firstNeighbor,
    index < points.length - 1 ? points[index + 1] : lastNeighbor,
  ];
  const directions: Point2D[] = [];
  for (const neighbor of neighbors) {
    if (!neighbor) continue;
    const dx = neighbor[0] - origin[0];
    const dy = neighbor[1] - origin[1];
    const length = Math.hypot(dx, dy);
    if (length > MIN_DIRECTION_LENGTH)
      directions.push([dx / length, dy / length]);
  }
  const [first, second] = directions;
  if (first && second) {
    const cross = first[0] * second[1] - first[1] * second[0];
    const dot = first[0] * second[0] + first[1] * second[1];
    const opening = Math.atan2(Math.abs(cross), dot);
    if ((opening * 180) / Math.PI >= MIN_BISECTOR_OPENING_DEGREES - 1e-8) {
      const halfTurn = (cross < 0 ? -opening : opening) / 2;
      directions.push([
        first[0] * Math.cos(halfTurn) - first[1] * Math.sin(halfTurn),
        first[0] * Math.sin(halfTurn) + first[1] * Math.cos(halfTurn),
      ]);
    }
  }
  return directions.length
    ? { origin: [...(pickupOrigin ?? origin)], directions }
    : undefined;
};

/** Constrain to the closest infinite guide in 0.1 m steps from its pickup origin. */
export const snapPointToDragGuides = (
  position: Point2D,
  guides: PointDragSnapGuides,
): Point2D => {
  const dx = position[0] - guides.origin[0];
  const dy = position[1] - guides.origin[1];
  let nearest = position;
  let nearestDistance = Infinity;
  for (const [nx, ny] of guides.directions) {
    const along = dx * nx + dy * ny;
    const candidate: Point2D = [
      guides.origin[0] + nx * along,
      guides.origin[1] + ny * along,
    ];
    const distance = Math.hypot(
      position[0] - candidate[0],
      position[1] - candidate[1],
    );
    if (distance < nearestDistance) {
      const snappedAlong = roundToDecimals(along, 1);
      nearest = [
        guides.origin[0] + nx * snappedAlong,
        guides.origin[1] + ny * snappedAlong,
      ];
      nearestDistance = distance;
    }
  }
  return nearest;
};
